import Image from '@tiptap/extension-image'
import type { MarkdownNodeSpec } from 'tiptap-markdown'

/**
 * Persisted image size marker, riding on the standard CommonMark image
 * "title" slot: `![alt](src "=WxH")`. Either dimension may be omitted
 * (`=x400`, `=800x`), but the marker itself must be the entire title —
 * a real user-authored title never collides with it because it always
 * starts with `=` immediately followed by an optional digit run, `x`,
 * and another optional digit run.
 */
const IMAGE_SIZE_TITLE_PATTERN = /^=(\d+)?x(\d+)?$/

export function parseImageSizeTitle(title: string | null): {
  width: number | null
  height: number | null
} {
  const match = title ? IMAGE_SIZE_TITLE_PATTERN.exec(title) : null
  if (!match) return { width: null, height: null }

  const [, widthText, heightText] = match
  return {
    width: widthText ? Number(widthText) : null,
    height: heightText ? Number(heightText) : null,
  }
}

export function formatImageSizeTitle(width: number | null, height: number | null): string | null {
  if (width === null && height === null) return null
  return `=${width ?? ''}x${height ?? ''}`
}

function toFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function readSizeAttribute(element: HTMLElement, attribute: 'width' | 'height'): number | null {
  const rawValue = element.getAttribute(attribute)
  if (rawValue !== null) {
    const parsed = Number(rawValue)
    if (Number.isFinite(parsed)) return parsed
  }
  return parseImageSizeTitle(element.getAttribute('title'))[attribute]
}

/**
 * Markdown serialize/parse override for the `image` node, replacing
 * tiptap-markdown's built-in spec so the `width`/`height` attrs round-trip
 * through the title's `=WxH` marker instead of being dropped.
 */
const sizedImageMarkdownSpec: MarkdownNodeSpec = {
  serialize(state, node) {
    const width = toFiniteNumber(node.attrs.width)
    const height = toFiniteNumber(node.attrs.height)
    const sizeTitle = formatImageSizeTitle(width, height)
    const title = sizeTitle ?? toNonEmptyString(node.attrs.title)
    const altText = toNonEmptyString(node.attrs.alt) ?? ''
    const srcUrl = toNonEmptyString(node.attrs.src) ?? ''

    const titlePart = title ? ` "${title.replace(/"/g, '\\"')}"` : ''
    state.write(`![${state.esc(altText)}](${srcUrl.replace(/[()]/g, '\\$&')}${titlePart})`)
    // Image is a block-level node (`inline: false`), so unlike the
    // built-in inline image spec, this one must close its own block —
    // otherwise a list right after the image gets its first bullet
    // marker glued onto the image's line and silently dropped on reparse.
    state.closeBlock(node)
  },
}

/**
 * `@tiptap/extension-image` with graphical resize enabled (first-party
 * `resize` option, no custom node view) and a `title`-based size marker so
 * the resized dimensions survive the markdown round-trip. See
 * `MarkdownView`'s `img` renderer for the read side of the marker.
 */
export const SizedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => readSizeAttribute(element, 'width'),
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => readSizeAttribute(element, 'height'),
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const title = element.getAttribute('title')
          // The size marker lives in `width`/`height` once parsed — never
          // surface it back as a literal user-visible title.
          return title && IMAGE_SIZE_TITLE_PATTERN.test(title) ? null : title
        },
      },
    }
  },

  addStorage() {
    return {
      ...this.parent?.(),
      markdown: sizedImageMarkdownSpec,
    }
  },
})
